import axios, {
  AxiosError,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
  Method,
  RawAxiosRequestHeaders,
  AxiosHeaders,
} from 'axios';
import { clearStorage } from '@/utils/storage.utils';
import { API_BASE_URL } from '@/utils/constant.utils';

type MethodsHeaders = Partial<
  {
    [Key in Method as Lowercase<Key>]: AxiosHeaders;
  } & { common: AxiosHeaders }
>;

export type HttpMethod = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH';
export type HttpRequest = {
  url: string;
  method?: HttpMethod;
  body?: object;
  headers?: (RawAxiosRequestHeaders & MethodsHeaders) | AxiosHeaders;
  baseUrl?: string;
  responseType?: string;
};

export enum HTTP_METHOD {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

export interface ErrorResponse {
  error: string;
  status: number;
  data: undefined;
}

const HTTP_STATUS_UNAUTHORIZED = 401;
const UNAUTHORIZED_TOKEN = 'UNAUTHORIZED_TOKEN';

const baseURL = API_BASE_URL;

export const axiosInstance = axios.create({
  baseURL,
});

axiosInstance.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      request.headers = request.headers as AxiosRequestHeaders;
      request.headers['Authorization'] = `Bearer ${token}`;
    }
    return request;
  },
  (error: AxiosError) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  <T>(response: AxiosResponse<T>): AxiosResponse<T> => response,
  async (error) => {
    if (
      error.response?.data?.statusCode === HTTP_STATUS_UNAUTHORIZED &&
      error.response?.data?.message === UNAUTHORIZED_TOKEN
    ) {
      clearStorage();
      window.location.href = '/login';
    }

    return Promise.reject({
      error: error?.response?.data?.message || error.message,
      status: error?.response?.statusCode || error?.statusCode,
      data: undefined,
    });
  }
);

export const requestApi = async (data: HttpRequest) => {
  return axiosInstance.request({
    url: data.url,
    method: data.method ? data.method : HTTP_METHOD.GET,
    data: data.body,
    headers: data.headers,
  });
};

export default requestApi;
